/**
 * The call report's pure parts: what a time-range preset means, and how a span of seconds reads.
 *
 * Separate from `routes/calls.tsx` for the same reason `paging.ts` is separate from the pickers —
 * these are the bits with edges worth testing (local midnight, the minute/hour boundary), and a
 * test runner cannot import a module that pulls in React to reach them.
 */

/**
 * Presets rather than two date pickers.
 *
 * Every question anyone actually asks of a call table is "today", "this week" or "this month",
 * and a report that opens on all of history gets slower every day it runs.
 */
export const RANGES = {
  today: "Today",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
} as const;

export type Range = keyof typeof RANGES;

/** What the report opens on. Wide enough to look populated on a quiet week, narrow enough that
 *  it stays fast as the table grows. */
export const DEFAULT_RANGE: Range = "7d";

/**
 * The `since` a preset means, as RFC 3339, or `undefined` for "all time".
 *
 * `today` is **local** midnight, not "24 hours ago": to a floor manager "today" means since
 * their shift started, and anchoring on UTC midnight would roll the report over at 5pm in
 * California — mid-afternoon, with half the day's calls silently dropping off the top.
 *
 * `now` is a parameter so this can be tested at a boundary rather than at whatever time the
 * suite happens to run.
 */
export function rangeSince(range: Range, now: Date = new Date()): string | undefined {
  if (range === "all") return undefined;
  if (range === "today") {
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    return midnight.toISOString();
  }
  const hours = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30 }[range];
  return new Date(now.getTime() - hours * 3600_000).toISOString();
}

/**
 * A span of seconds as a person says it: "4h 21m", "6m 30s", "12s".
 *
 * Distinct from `formatDuration`, which is `mm:ss` for a call timer. The two are not
 * interchangeable: a running call wants a clock that ticks, and 15,660 seconds of total talk
 * time rendered as `261:00` is unreadable.
 */
export function formatSpan(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${s % 60}s`;
}

/**
 * Which half of my own day this leg is: one I placed, or one I was rung for.
 *
 * Mirrors `own_leg_direction` in core-api's `scope.py`, which is what the filter beneath the
 * table narrows on — the label on a row and the filter above it must not be able to disagree.
 *
 * The switch's own `direction` is deliberately not consulted. It is the *channel's* direction
 * and means "did this channel arrive at the switch or did the switch create it", which is a
 * different question with a different answer: the console places a call by ringing the rep
 * first, so a call they placed arrives here stamped `outbound`. The caller column is what
 * survives that — on a leg my phone was on, my own number is the caller when I dialled and the
 * far party's when I was rung.
 *
 * `null` for a leg with neither of my numbers on it. Rare — the leg is mine because the switch
 * said the channel was my phone's, and its caller ids were rewritten in between — and "—" is a
 * better answer there than a coin flip presented as a fact.
 */
export function myDirection(
  leg: { caller_number: string | null; callee_number: string | null; kind?: string | null },
  mine: readonly string[],
): "placed" | "received" | null {
  if (leg.caller_number !== null && mine.includes(leg.caller_number)) return "placed";
  if (leg.callee_number !== null && mine.includes(leg.callee_number)) {
    // A call the dialer placed for me — a preview offer I dialed, a number I typed (ADR-047),
    // a power dial — rings my phone with the lead as the caller, exactly like a call they
    // placed to me. The kind the ACD stamped on the leg is the one thing that says which.
    return leg.kind === "outbound" ? "placed" : "received";
  }
  return null;
}

/**
 * Who placed one of my legs, for the sentence on it: me from my own phone, me through the
 * dialer (a preview offer, a typed number), the dialer on its own (a power dial), or the far
 * party. Null when `myDirection` can say nothing.
 */
export function placedBy(
  leg: {
    caller_number: string | null;
    callee_number: string | null;
    kind?: string | null;
    dial_mode?: string | null;
  },
  mine: readonly string[],
): "you" | "dialer" | "them" | null {
  const direction = myDirection(leg, mine);
  if (direction === null) return null;
  if (direction === "received") return "them";
  return leg.kind === "outbound" && leg.dial_mode === "power" ? "dialer" : "you";
}

/**
 * The party at the *other* end of one of my legs.
 *
 * A CDR names a caller and a callee, and which of those is "me" flips with how the call
 * started: the ACD *originates* the rep's leg, so the rep is the callee; a rep dialling from
 * the softphone arrives at the switch as the caller. A history column that simply showed
 * `caller_number` would therefore be the rep's own extension on half their day — a column of
 * their own number, which is the least useful column a call history could have.
 *
 * Direction cannot settle it either: both of those legs are stamped `outbound`, and the party
 * on them is the callee on one and the caller on the other.
 *
 * `mine` is `sip_endpoint_numbers` from `/auth/me` — every identifier the signed-in user's
 * phone answers to, which is the same set core-api matched the leg on in the first place. So
 * "the side that is not me" is exact rather than a guess, and it is exact for the aliased case
 * too: an endpoint that registers as `1001` and dials as `701` carries either on the wire.
 *
 * Both sides mine (a rep calling their own room) or neither (a row whose caller ids were
 * rewritten on the way through) leaves nothing to subtract, and the direction fallback is then
 * the best available reading rather than a claim.
 *
 * Whichever side it lands on, the name is our own contact record's where the lead is that party
 * (`lead_side`), and the switch's otherwise. Ours is worth preferring: the trunk sends no
 * caller-id name at all, so on an inbound call the switch has nothing to offer and the only
 * thing that can name the caller is the firm's own record of them.
 */
export function counterparty<
  T extends {
    direction: string | null;
    caller_number: string | null;
    caller_name: string | null;
    callee_number: string | null;
    callee_name: string | null;
    lead_name?: string | null;
    lead_side?: "caller" | "callee" | null;
  },
>(leg: T, mine: readonly string[]): { number: string | null; name: string | null } {
  const named = (side: "caller" | "callee", fallback: string | null) =>
    (leg.lead_side === side ? leg.lead_name : null) || fallback;
  const caller = { number: leg.caller_number, name: named("caller", leg.caller_name) };
  const callee = { number: leg.callee_number, name: named("callee", leg.callee_name) };

  const callerIsMine = leg.caller_number !== null && mine.includes(leg.caller_number);
  const calleeIsMine = leg.callee_number !== null && mine.includes(leg.callee_number);
  if (callerIsMine !== calleeIsMine) return callerIsMine ? callee : caller;

  // Nothing left to subtract, so read the channel: `direction` is the switch's own, and on a
  // leg my phone was on, `inbound` is the channel my phone dialled in with — the far end of
  // it is whoever I dialled — while `outbound` is the channel the switch rang me with, whose
  // far end is the caller.
  return leg.direction === "inbound" ? callee : caller;
}
